import { jsx, jsxs } from "react/jsx-runtime";
import { string, bool, func, oneOf, oneOfType, element, object, shape } from "prop-types";
import chevronRightSmall from "@ingka/ssr-icon/paths/chevron-right-small";
import chevronRight from "@ingka/ssr-icon/paths/chevron-right";
import arrowRight from "@ingka/ssr-icon/paths/arrow-right";
import linkOut from "@ingka/ssr-icon/paths/link-out";
import copy from "@ingka/ssr-icon/paths/copy";
import Checkbox from "@ingka/checkbox";
import RadioButton from "@ingka/radio-button";
import Switch from "@ingka/switch";
import SSRIcon from "@ingka/ssr-icon";
import Image from "@ingka/image";
import AspectRatioBox from "@ingka/aspect-ratio-box";
import PaymentLogo from "@ingka/payment-logo";
import React, { forwardRef } from "react";
import cx from "classnames";
const propTypes = {
  /** One of: [`checkbox`, `radiobutton`, `switch`, `navigational`]. Not selecting control will render button without trailing icon */
  control: oneOf(
    ["default", "checkbox", "radiobutton", "switch", "navigational", "link"]
  ),
  /** Callback for the `navigational` variant */
  onClick: func,
  /** Callback for the Radio, Checkbox or Switch */
  onChange: func,
  /** Function returning react element with paths for SSRIcon from `@ingka/ssr-icon/paths/[icon].js` */
  leadingIcon: func,
  leadingIconPosition: oneOf(["top", "center"]),
  /** Sets other attributes for `@ingka/image` */
  imageProps: object,
  /** Value for `src` attribute for for `@ingka/image` component */
  image: string,
  /** Alternative text for for `@ingka/image` component */
  imageAltText: string,
  /** PaymentLogo */
  paymentLogo: shape({
    src: string,
    alt: string
  }),
  size: oneOf(["small", "medium", "large"]),
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Toggles the `inset` variant */
  inset: bool,
  /** Toggles the `emphasised` variant */
  emphasised: bool,
  /** Main title text */
  title: oneOfType([string, element]),
  /** Description element text */
  description: oneOfType([string, element]),
  /** Toggles the disabled state */
  disabled: bool,
  /** Can be used to add classes to base LI element. */
  className: string,
  /** Overwrites id set by ListView */
  id: string,
  /** Sets other props to RadioButton, Checkbox and Switch */
  controlProps: shape({
    value: string.isRequired
  }),
  controlIcon: oneOf(["chevronRight", "chevronRightSmall", "arrowRight", "linkOut", "copy", null]),
  name: string,
  quantityLabel: string,
  /** ListView Item hypertext reference (link destination) */
  href: string,
  /** ListView Item hypertext reference (target attribute) */
  newWindow: string,
  addOn: oneOfType([string, element]),
  showDivider: bool
};
const ListViewItem = forwardRef((props, ref) => {
  const {
    prefix = "",
    id,
    control = "default",
    name,
    onClick,
    onChange,
    title,
    description,
    addOn,
    leadingIcon,
    image,
    imageAltText = "",
    paymentLogo,
    size = "medium",
    inset,
    emphasised,
    disabled,
    className,
    controlProps,
    imageProps,
    quantityLabel,
    href,
    newWindow,
    controlIcon = "linkOut",
    leadingIconPosition = "center",
    showDivider = true,
    ...otherProps
  } = props;
  let elementTag;
  switch (control) {
    case "navigational":
      elementTag = "button";
      break;
    case "link":
      elementTag = "a";
      break;
    case "default":
      elementTag = "span";
      break;
    default:
      elementTag = "label";
      break;
  }
  const baseClass = `${prefix}list-view-item`;
  const controlId = id + "__control";
  const controlTypes = {
    DEFAULT: "default",
    CHECKBOX: "checkbox",
    RADIOBUTTON: "radiobutton",
    SWITCH: "switch",
    NAVIGATIONAL: "navigational",
    LINK: "link"
  };
  const classes = cx(
    baseClass,
    disabled && control !== controlTypes.DEFAULT && `${baseClass}--disabled`,
    !showDivider && `${baseClass}__no-divider`,
    className
  );
  const itemClasses = cx(
    control === controlTypes.DEFAULT ? `${baseClass}__default` : `${baseClass}__action`,
    inset && `${baseClass}__inset`,
    image && `${baseClass}__with-image`
  );
  const trailingIcon = {
    chevronRight,
    chevronRightSmall,
    arrowRight,
    linkOut,
    copy
  };
  const TrailingIcon = control === "navigational" || control === "link" ? trailingIcon[controlIcon] && /* @__PURE__ */ jsx("span", { className: `${baseClass}__control-icon`, children: /* @__PURE__ */ jsx(SSRIcon, { prefix, paths: trailingIcon[controlIcon] }) }) : null;
  const formComponentProps = {
    subtle: true,
    id: controlId,
    name,
    disabled,
    onChange,
    prefix,
    ...controlProps
  };
  let FormControl;
  if (control === "checkbox") {
    FormControl = /* @__PURE__ */ jsx(Checkbox, { ...formComponentProps });
  } else if (control === "radiobutton") {
    FormControl = /* @__PURE__ */ jsx(RadioButton, { ...formComponentProps });
  } else if (control === "switch") {
    FormControl = /* @__PURE__ */ jsx(Switch, { ...formComponentProps });
  }
  const leadingOptsMap = {
    ["image"]: /* @__PURE__ */ jsx("span", { className: `${baseClass}__image`, children: /* @__PURE__ */ jsx(AspectRatioBox, { prefix, ratio: "square", children: /* @__PURE__ */ jsx(Image, { src: image, prefix, alt: imageAltText, ...imageProps }) }) }),
    ["icon"]: /* @__PURE__ */ jsx(SSRIcon, { prefix, className: `${baseClass}__icon-${leadingIconPosition}`, paths: leadingIcon }),
    ["paymentLogo"]: /* @__PURE__ */ jsx(
      PaymentLogo,
      {
        prefix,
        className: `${baseClass}__payment-logo`,
        size: size === "small" ? "small" : "medium",
        ...paymentLogo
      }
    )
  };
  let leadingMedia = null;
  if (image) {
    leadingMedia = leadingOptsMap.image;
  } else if (paymentLogo && !image) {
    leadingMedia = leadingOptsMap.paymentLogo;
  } else if (leadingIcon && !image && !paymentLogo) {
    leadingMedia = leadingOptsMap.icon;
  }
  const ActionElementTag = elementTag;
  return /* @__PURE__ */ jsx(
    "li",
    {
      id,
      ref,
      className: classes,
      children: /* @__PURE__ */ jsxs(
        ActionElementTag,
        {
          className: itemClasses,
          onClick: !disabled && control === controlTypes.NAVIGATIONAL ? onClick : null,
          href: control === controlTypes.LINK && href && !disabled ? href : null,
          target: control === controlTypes.LINK && href && newWindow && !disabled ? newWindow : null,
          ...otherProps,
          disabled: control === controlTypes.NAVIGATIONAL && disabled,
          children: [
            /* @__PURE__ */ jsxs("span", { className: `${baseClass}__wrapper`, children: [
              leadingMedia,
              /* @__PURE__ */ jsxs("span", { className: `${baseClass}__label-container`, children: [
                /* @__PURE__ */ jsx("span", { className: cx(`${baseClass}__title`, emphasised && `${baseClass}__title--emphasised`), children: title }),
                description && size !== "small" && /* @__PURE__ */ jsx("span", { className: `${baseClass}__description`, children: description }),
                addOn && size !== "small" && /* @__PURE__ */ jsx("span", { className: `${baseClass}__addon`, children: addOn })
              ] })
            ] }),
            quantityLabel && /* @__PURE__ */ jsx("span", { className: `${baseClass}__quantity-label`, children: quantityLabel }),
            TrailingIcon ? TrailingIcon : FormControl
          ]
        }
      )
    }
  );
});
ListViewItem.propTypes = propTypes;
ListViewItem.displayName = "ListViewItem";
var ListViewItem_default = ListViewItem;
export {
  ListViewItem_default as default
};
